import React, { useMemo, useEffect, useState } from "react";
import { Box, AppBar, Toolbar, Link, Typography, IconButton, Button, Avatar } from "@mui/material";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { useProject } from "../hook/useProject";
import { BrowserView, isBrowser, MobileView } from "react-device-detect";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import CloseIcon from "@mui/icons-material/Close";
import { useUser } from "../hook/useUser";

const AppHeader = ({ showSideBar, setShowSideBar, setShowProjectDetails, setShowMyProfile, headerHeight, headerRightButtonRef }) => {
  const theme = useTheme();
  const { moduleName, projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const project = useProject(projectId);
  const [forceUpdate, setForceUpdate] = useState(false);

  useEffect(() => {
    if (headerRightButtonRef.current) {
      setForceUpdate((prev) => !prev);
    }
  }, [showSideBar, headerRightButtonRef]);

  const logout = () => {
    window.localStorage.removeItem("token");
    window.localStorage.removeItem("refreshToken");
    window.localStorage.removeItem("exp");
    navigate(`/`);
  };

  const appBarStyles = useMemo(
    () => ({
      position: "fixed",
      zIndex: `${theme.zIndex.drawer + 1} !important`,
      boxShadow: "none",
      height: headerHeight,
    }),
    [theme]
  );

  const toolbarStyles = useMemo(
    () => ({
      minWidth: 768,
      height: "76px !important",
      overflowY: "visible",
      backgroundColor: theme.palette.background.default,
      display: "flex",
      justifyContent: "space-between",
    }),
    [theme]
  );

  const inputStyle = {
    display: "flex",
    alignItems: "center",
    height: 34,
    backgroundColor: "#B9D9EB",
    borderRadius: "10px",
    border: "2px solid #fff",
    ml: 3,
    paddingX: 1.5,
    gap: 2,
  };

  const navigateToPage = () => {
    navigate(`/modules/Project`);
  };

  const renderMainAppBar = () => (
    <BrowserView>
      <AppBar sx={appBarStyles}>
        <Toolbar sx={toolbarStyles}>
          <Box display="flex" alignItems="center">
            <img height={40} alt="logo" src="assets/illustrations/clp.png" />
          </Box>
        </Toolbar>
      </AppBar>
    </BrowserView>
  );

  const renderProjectsAppBar = () => (
    <AppBar sx={appBarStyles}>
      <Toolbar sx={{ color: "black", minHeight: 40, px: 1, backgroundColor: theme.palette.background.default }}>
        <IconButton onClick={() => setShowMyProfile(true)}>
          <Avatar sx={{ width: 30, height: 30, bgcolor: theme.palette.primary.main }}>
            {user?.firstName?.charAt(0)}
            {user?.lastName?.charAt(0)}
          </Avatar>
        </IconButton>
        <Box display="flex" justifyContent="center" sx={{ flexGrow: 1 }}>
          <Box width={200} display="flex">
            <Box sx={{ flexGrow: 1, borderBottom: `solid 2px ${theme.palette.secondary.main}` }}>
              <Typography sx={{ fontSize: 16, textAlign: "center" }}>My Projects</Typography>
            </Box>
            <img src="assets/background/headerbg.png" alt="header" style={{ width: 28, height: 28 }}></img>
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );

  const renderModuleAppBar = () => (
    <AppBar sx={appBarStyles}>
      <BrowserView>
        <Box sx={{ backgroundColor: theme.palette.primary.main, height: 28, width: "100%", textAlign: "right", pr: 3 }}>
          <Box component="span" sx={{ color: "#fff", fontSize: theme.typography.fontSize, lineHeight: "28px" }}>
            hello, {user?.firstName} {user?.lastName} ({user?.company})&nbsp;&nbsp;&nbsp;|&nbsp;&nbsp;&nbsp;
            <Button variant="text" onClick={logout} sx={{ color: "white" }}>
              Logout
            </Button>
          </Box>
        </Box>
        <Box sx={{ backgroundColor: "#B9D9EB", height: 48, width: "100%", display: "flex", alignItems: "center" }}>
          <Box display="flex" alignItems="center" sx={{ backgroundColor: "#fff", width: 180, height: "100%", pl: 3 }}>
            <Link component={RouterLink} to="/" sx={{ display: "contents" }}>
              <img height={32} alt="logotext" src="assets/illustrations/logotext.png" />
              <img height={48} width={150} alt="logotext" src="assets/illustrations/logotext2.png" />
              <img height={48} alt="triangle" src="assets/illustrations/triangle.png" />
            </Link>
          </Box>
          {projectId && project && (
            <Box sx={inputStyle}>
              <img height={24} src="assets/icons/projects.svg" alt="projects" />
              <Typography variant="h6" component="span">
                {project.projectName}
              </Typography>
              <IconButton onClick={() => setShowProjectDetails(true)}>
                <img height={24} src="assets/icons/ellipsiscircle.svg" alt="ellipsiscircle" />
              </IconButton>
            </Box>
          )}
        </Box>
      </BrowserView>
      <MobileView>
        <Toolbar sx={{ color: "white", minHeight: headerHeight, px: 1 }}>
          <IconButton
            sx={{ color: "white" }}
            onClick={() => {
              setShowSideBar(!showSideBar);
            }}
          >
            {showSideBar ? <CloseIcon /> : <FormatListBulletedIcon />}
          </IconButton>
          <Button sx={{ color: "white", flexGrow: 1 }} endIcon={<img src={"assets/icons/sort.svg"} alt={"sort"} style={{ height: 16, width: 16 }} />}>
            <Typography sx={{ fontSize: 16 }}>{project?.projectName}</Typography>
          </Button>
          {headerRightButtonRef.current && (
            <IconButton key={forceUpdate} sx={{ color: "white" }} onClick={headerRightButtonRef.current.onClick}>
              <img src={headerRightButtonRef.current.icon} alt={"tune"} style={{ height: 18, width: 18 }} />
            </IconButton>
          )}
        </Toolbar>
      </MobileView>
    </AppBar>
  );

  if (isBrowser) {
    return moduleName ? renderModuleAppBar() : renderMainAppBar();
  } else {
    return moduleName && projectId ? renderModuleAppBar() : moduleName ? renderProjectsAppBar() : renderMainAppBar();
  }
};

export default AppHeader;
